// export type ReportParametersUnion;

import {
   AbstractControl,
   UntypedFormArray,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { LcsDateRange } from '@lcs/shared/core';
import { ColumnSortReportParameter } from '@ndt/report-output/shared/models/column-sort-report-parameter';
import { DateRangeReportParameter } from '@ndt/report-output/shared/models/date-range-report-parameter';
import { DateRangeSelectReportParameter } from '@ndt/report-output/shared/models/date-range-select-report-parameter';
import { MultiEntitySelectionReportParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { SelectedAccountReportParameter } from '@ndt/report-output/shared/models/selected-account-report-parameter';
import { createTimeRangeReportParameter } from '@ndt/report-output/shared/models/time-range-report-parameter-value';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { ExtensionGroupOption } from 'src/app/common-components/entity-select/extension-group/models/extension-group-option';
import { PhoneNumberOption } from 'src/app/common-components/entity-select/phonenumber/models/phone-number-option';
import { TileSetType } from 'src/app/pages/dashboard/tile-set-type-select/models/tile-set.model';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { TimeRangeReportParameter } from 'src/app/pages/tiles/views/ndt-wait-times-graph-tile/models/wait-times-tile';
import { CallDirectionsToIncludeReportParameterValue } from '../../models/call-directions-to-include-report-parameter-value';
import { CallDispositionsToIncludeReportParameterValue } from '../../models/call-dispositions-to-include-report-parameter-value';
import { createDateRangeReportParameter } from '../../models/date-range-report-parameter-value';
import { DaysOfWeekReportParameterValue } from '../../models/day-of-week-report-parameter-value';
import { getParentMultiSelectRequiredValidator } from '../report-parameter-validator-helper';
import { ParameterFormValueUnion } from './report-parameter-form-value';

export type ReportParametersUnion =
   | DateRangeSelectParameter
   | DateRangeParameter
   | CallDirectionsToIncludeReportParameter
   | CallDispositionsToIncludeReportParameter
   | DaysOfWeekReportParameter
   | EntitiesToIncludeParameter
   | BooleanReportParameter
   | TimeZoneParameter
   | SelectedAccountParameter
   | ColumnSortParameter
   | NumberOfCallersParameter
   | SortByParameter
   | ExtensionSortByParameter
   | BarGraphSourceParameter
   | TileLayoutConfigurationNameParameter
   | TileLayoutConfigurationColorParameter
   | TileLayoutConfigurationRefreshTimeSecondsParameter
   | TileLayoutConfigurationTileSetType
   | TimeRangeParameter;

interface ParameterBase {
   // TODO: not memorizing yet
   isAskParameter?: boolean;
   parameterType: ViewReportParameters;
   sortOrder: number | null;
   hidden?: boolean;
}

export enum TileLayoutConfigurationReportParameters {
   Name = 'TileLayoutConfigurationName',
   Color = 'TileLayoutConfigurationColor',
   RefreshTimeSeconds = 'TileLayoutConfigurationRefreshTimeSeconds',
   TileSetType = 'TileLayoutConfigurationTileSetType',
}

export type ViewReportParameters = ReportParameters | TileLayoutConfigurationReportParameters;

export type BooleanReportParameters =
   | ReportParameters.ExcludeInternal
   | ReportParameters.IncludeNotMissedCalls
   | ReportParameters.SummaryOnly
   | ReportParameters.GroupByExtension
   | ReportParameters.ShowDate
   | ReportParameters.AggregateQueues;

export type EntitiesToIncludeParameters =
   | ReportParameters.ExtensionsToInclude
   | ReportParameters.PhoneNumbersToInclude
   | ReportParameters.ExtensionGroupsToInclude
   | ReportParameters.AutoAttendantExtensionsToInclude
   | ReportParameters.CallQueueExtensionsToInclude;

export type DateRangeParameters = ReportParameters.DateRange;

export type SelectedAccountParameters = ReportParameters.SelectedAccount;

export type TimeZoneParameters = ReportParameters.TimeZone;

export type NumberOfCallersParameters = ReportParameters.NumberOfCallers;

export type SortByParameters = ReportParameters.SortBy;

export type ExtensionSortByParameters = ReportParameters.ExtensionSortBy;

export type BarGraphSourceParameters = ReportParameters.BarGraphSource;

export type TimeRangeParameters = ReportParameters.TimeRange;

export type ColumnSortParameters = ReportParameters.ColumnSort;

export interface DateRangeSelectParameter extends ParameterBase, DateRangeSelectReportParameter {
   parameterType: ReportParameters.DateRangeSelect;
}

export interface DateRangeParameter extends ParameterBase, DateRangeReportParameter {
   parameterType: DateRangeParameters;
}

export interface SelectedAccountParameter extends ParameterBase, SelectedAccountReportParameter {
   parameterType: SelectedAccountParameters;
}

export interface ColumnSortParameter extends ParameterBase, ColumnSortReportParameter {
   parameterType: ColumnSortParameters;
}

export interface TimeRangeParameter extends ParameterBase, TimeRangeReportParameter {
   parameterType: TimeRangeParameters;
}

export interface CallDirectionsToIncludeReportParameter
   extends ParameterBase,
      CallDirectionsToIncludeReportParameterValue {
   parameterType: ReportParameters.CallDirectionsToInclude;
}

export interface CallDispositionsToIncludeReportParameter
   extends ParameterBase,
      CallDispositionsToIncludeReportParameterValue {
   parameterType: ReportParameters.CallDispositionsToInclude;
}

export interface DaysOfWeekReportParameter extends ParameterBase, DaysOfWeekReportParameterValue {
   parameterType: ReportParameters.DaysOfTheWeek;
}

export interface EntitiesToIncludeParameter extends ParameterBase, MultiEntitySelectionReportParameter {
   parameterType: EntitiesToIncludeParameters;
   names: string[];
}

export interface EntitiesToIncludeParameter extends ParameterBase, MultiEntitySelectionReportParameter {
   parameterType: EntitiesToIncludeParameters;
   names: string[];
}

export interface BooleanReportParameter extends ParameterBase {
   parameterType: BooleanReportParameters;
   value: boolean;
}

export interface TimeZoneParameter extends ParameterBase, TimeZoneReportParameter {
   parameterType: TimeZoneParameters;
   value: string;
}

export interface NumberOfCallersParameter extends ParameterBase {
   parameterType: NumberOfCallersParameters;
   value: number;
}

export interface SortByParameter extends ParameterBase {
   parameterType: SortByParameters;
   value: string;
}

export interface ExtensionSortByParameter extends ParameterBase {
   parameterType: ExtensionSortByParameters;
   value: string;
}

export interface BarGraphSourceParameter extends ParameterBase {
   parameterType: BarGraphSourceParameters;
   value: string;
}

export interface TileLayoutConfigurationNameParameter extends ParameterBase {
   parameterType: TileLayoutConfigurationReportParameters.Name;
   name: string;
}

export interface TileLayoutConfigurationColorParameter extends ParameterBase {
   parameterType: TileLayoutConfigurationReportParameters.Color;
   color: string;
}

export interface TileLayoutConfigurationRefreshTimeSecondsParameter extends ParameterBase {
   parameterType: TileLayoutConfigurationReportParameters.RefreshTimeSeconds;
   refreshTimeSeconds: number | null;
}

export interface TileLayoutConfigurationTileSetType extends ParameterBase {
   parameterType: TileLayoutConfigurationReportParameters.TileSetType;
   tileSetType: TileSetType | null;
}

export function convertToForm(values: ReportParametersUnion[], fb: UntypedFormBuilder): UntypedFormGroup {
   const array: UntypedFormArray = convertToFormArray(values, fb);
   return fb.group({ values: array });
}

export function convertToFormArray(values: ReportParametersUnion[], fb: UntypedFormBuilder): UntypedFormArray {
   const groups = values.map((v) => {
      const formGroupModel = convertToGroup(v, fb);
      return fb.group(formGroupModel);
   });
   return fb.array(groups);
}

export function convertToGroup(value: ReportParametersUnion, fb: UntypedFormBuilder, ignoreAskInValidators = true) {
   let formModel: ParameterFormValueUnion;
   switch (value.parameterType) {
      case ReportParameters.ExtensionsToInclude: {
         const extensions = value.values?.map((id, index) => {
            const extension: ExtensionOption = {
               extensionId: id,
               name: value.names[index],
               number: null,
               type: undefined,
            };
            return extension;
         });
         formModel = {
            accountId: value.accountId ?? 0,
            all: !!value.all,
            extensions,
            parameterType: ReportParameters.ExtensionsToInclude,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.PhoneNumbersToInclude: {
         const phoneNumbers = value.values?.map((id, index) => {
            const phoneNumber: PhoneNumberOption = {
               phoneNumberId: id,
               displayName: '',
               phoneNumber: `${value.values[index]}`,
               allowOutboundCalls: false,
            };
            return phoneNumber;
         });
         formModel = {
            accountId: value.accountId ?? 0,
            all: !!value.all,
            phoneNumbers,
            parameterType: ReportParameters.PhoneNumbersToInclude,
            sortOrder: value.sortOrder,
         };
         return {
            ...formModel,
            phoneNumbers: fb.control(phoneNumbers, {
               validators: getPhoneNumberValidator(),
            }),
         };
      }

      case ReportParameters.TimeRange: {
         formModel = {
            ...createTimeRangeReportParameter(value),
            isAskParameter: !!value.isAskParameter,
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.ExtensionGroupsToInclude: {
         const extensionGroups = value.values?.map((id, index) => {
            const extensionGroup: ExtensionGroupOption = {
               extensionGroupId: id,
               name: value.names[index],
               extensionGroupExtensions: [],
            };
            return extensionGroup;
         });
         formModel = {
            accountId: value.accountId ?? 0,
            all: !!value.all,
            extensionGroups,
            parameterType: ReportParameters.ExtensionGroupsToInclude,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }

      case ReportParameters.ColumnSort:
         formModel = {
            parameterType: ReportParameters.ColumnSort,
            sortingOrder: value.sortingOrder,
            sortOrder: value.sortOrder,
            value: `${value.value}` || '',
         };
         return formModel;
      case ReportParameters.CallQueueExtensionsToInclude: {
         const extensions = value.values?.map((id, index) => {
            const extension: ExtensionOption = {
               extensionId: id,
               name: value.names[index],
            };
            return extension;
         });
         formModel = {
            accountId: value.accountId ?? 0,
            all: !!value.all,
            extensions: fb.array(
               extensions.map((e) =>
                  fb.group({
                     extensionId: [e.extensionId],
                     name: [e.name],
                  })
               )
            ) as any,
            parameterType: ReportParameters.CallQueueExtensionsToInclude,
            sortOrder: value.sortOrder,
         };
         return {
            ...formModel,
            extensions: fb.control(extensions ?? []),
         };
      }
      case ReportParameters.AutoAttendantExtensionsToInclude: {
         const extensions = value.values?.map((id, index) => {
            const extension: ExtensionOption = {
               extensionId: id,
               name: value.names[index],
            };
            return extension;
         });
         formModel = {
            accountId: value.accountId ?? 0,
            all: !!value.all,
            extensions,
            parameterType: ReportParameters.AutoAttendantExtensionsToInclude,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.CallDirectionsToInclude:
         formModel = {
            parameterType: ReportParameters.CallDirectionsToInclude,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      case ReportParameters.CallDispositionsToInclude: {
         formModel = {
            parameterType: ReportParameters.CallDispositionsToInclude,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.ExcludeInternal:
      case ReportParameters.ShowDate:
      case ReportParameters.AggregateQueues:
         formModel = {
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
            value: value.value,
         };
         return formModel;
      case ReportParameters.DateRange: {
         formModel = {
            ...createDateRangeReportParameter(value),
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }

      case ReportParameters.DateRangeSelect: {
         formModel = {
            ...createDateRangeReportParameter(value),
            field: value.field,
            filterByDateRange: value.filterByDateRange ?? false,
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.SummaryOnly: {
         formModel = {
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
            value: value.value,
         };
         return formModel;
      }
      case ReportParameters.TimeZone: {
         formModel = {
            parameterType: value.parameterType,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.SelectedAccount: {
         formModel = {
            parameterType: value.parameterType,
            value: value.value,
            name: value.name,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.DaysOfTheWeek: {
         formModel = {
            parameterType: value.parameterType,
            values: [...value.values],
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.NumberOfCallers: {
         formModel = {
            parameterType: value.parameterType,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.SortBy: {
         formModel = {
            parameterType: value.parameterType,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.ExtensionSortBy: {
         formModel = {
            parameterType: ReportParameters.ExtensionSortBy,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.IncludeNotMissedCalls: {
         formModel = {
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
            value: value.value,
         };
         return formModel;
      }
      case ReportParameters.BarGraphSource: {
         formModel = {
            parameterType: value.parameterType,
            value: value.value,
            sortOrder: value.sortOrder,
         };
         return formModel;
      }
      case ReportParameters.GroupByExtension:
         formModel = {
            parameterType: value.parameterType,
            sortOrder: value.sortOrder,
            value: value.value,
         };
         return formModel;

      default:
         throw new Error('Unknown parameter type: ' + value);
   }
}

export function convertToDateRangeReportParameter(
   unionValues: ReportParametersUnion[],
   dateAdapter: LcsDateTimeAdapter<Date>,
   nthDateRangeParameter = 1
): DateRangeReportParameter | null {
   let n = 0;
   if (unionValues.length) {
      for (let i = 0; i <= unionValues.length; i++) {
         const parameter = unionValues[i];

         if (parameter.parameterType === ReportParameters.DateRange) {
            if (++n === nthDateRangeParameter) {
               let dateRangeReportParameter: DateRangeReportParameter = {
                  endDate: parameter.endDate,
                  startDate: parameter.startDate,
               };

               const dateRange = convertToStartDayEndDay({ ...dateRangeReportParameter }, dateAdapter);
               dateRangeReportParameter = { ...dateRangeReportParameter, ...dateRange };

               return dateRangeReportParameter;
            }
         }
      }
   }

   return null;
}

export function convertToStartDayEndDay(
   {
      startDate,
      endDate,
   }: {
      startDate?: Date | string | null;
      endDate?: Date | string | null;
   },
   dateAdapter: LcsDateTimeAdapter<Date>
): LcsDateRange<Date> {
   return {
      endDate: endDate ? dateAdapter.getDayEnd(new Date(endDate)) : null,
      startDate: startDate ? dateAdapter.getDayStart(new Date(startDate)) : null,
   };
}

export function getPhoneNumberValidator(): ValidatorFn {
   return (control: AbstractControl) => {
      const parent = control.parent;
      if (parent) {
         parent.addValidators([Validators.required, getParentMultiSelectRequiredValidator()]);
      }
      return null;
   };
}
